'use client';
import classNames from 'classnames';
import { CldImage } from 'next-cloudinary';
import { useState } from 'react';
import AnimationImage from '../animation-image/animation-image';
import CardSquare from '../card-square/card-square';

type CaseStudyItemPropTypes = {
    summary: string;
    imageUrl: string;
    title: string;
    year?: number;
    mode?: 'dark' | 'light';
    itemSize?: 'small' | 'large';
};

export const CaseStudyItem = (props: CaseStudyItemPropTypes) => {
    const { mode = 'light', summary, imageUrl, title, year, itemSize = 'large' } = props;
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const textClasses = classNames({
        'text-white': mode === 'dark',
        'text-gray-900': mode === 'light',
    });

    const emdashClasses = classNames({
        'emdash-push--dark': mode === 'dark',
    });

    if (itemSize === 'large') {
        return (
            <div className="items-center grid-cols-1 gap-0 mx-6 mb-24 group md:mx-0 md:grid md:grid-cols-14 md:gap-10">
                <div className="col-span-4">
                    <div className="mb-2 space-y-2 md:mb-8">
                        <div className={`${textClasses} h5 overline`}>Case Study{year ? ` • ${year}` : ''}</div>
                        {title && <div className={`${textClasses} h-lg group-hover:text-green-400`}>{title}</div>}
                        <p className={`${textClasses}`}>{summary}</p>
                    </div>
                    <div className={`inline-block ${textClasses} h5 emdash-push ${emdashClasses}`}>Read More</div>
                </div>
                {imageUrl && (
                    <div className="col-span-10 mt-6 md:mt-0">
                        <AnimationImage isImageLoaded={isImageLoaded}>
                            <div className="relative w-full min-h-[370px] md:min-h-[550px]">
                                <CldImage
                                    fill
                                    sizes="(max-width: 768px) 100vw, 66vw"
                                    className="object-cover w-full min-h-[370px] md:min-h-[550px]"
                                    src={`${imageUrl}`}
                                    onLoad={() => setIsImageLoaded(true)}
                                    alt=""
                                    loading="lazy"
                                    format="auto" // Automatically use WebP or AVIF based on browser support
                                    quality="auto" // Automatically adjust quality based on network and device
                                    dpr="auto" // Automatically adjust for device pixel ratio
                                    fetchPriority="auto"
                                    placeholder="blur"
                                    blurDataURL="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=="
                                />
                            </div>
                        </AnimationImage>
                    </div>
                )}
            </div>
        );
    }

    return (
        <CardSquare
            mode="light"
            overline={`Case Study`}
            summary={summary || ''}
            title={title}
            imageUrl={`${imageUrl}`}
            author=""
            imageAltText=""
            year={year}
        />
    );
};

export default CaseStudyItem;
